import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { SAGE, CREAM } from '../../../theme';
import { getSession } from '../../../services/interviewService';
import InterviewModal from '../../interview/InterviewModal';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

/**
 * Dashboard "continue" card — the heart of the ~5-min return loop (design §12).
 * For a new user it invites them to build their profile; for a returning user it
 * shows progress + the next suggested bite. A quick item opens the modal (stay on
 * the dashboard); "continue at length" opens the dedicated page.
 */
const InterviewContinueCard = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    getSession()
      .then((s) => setSession(s && s.exists ? s : null))
      .catch(() => setSession(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const openHooks = (session?.frontier || []).filter((h) => h.status === 'open');
  const nextHook = session?.next_hook || openHooks[0] || null;
  const started = !!session;

  const kind = started ? 'Continue' : 'Get started';
  const title = started ? 'Continue your profile' : 'Build your profile';
  const sub = started
    ? (nextHook
        ? `Next up: ${nextHook.label}. About 5 minutes — stop anytime.`
        : 'Pick up where you left off — about 5 minutes.')
    : 'A calm, ~5-minute conversation to capture the shape of your career. Depth comes later.';

  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', bgcolor: CREAM[50], borderRadius: 3,
        border: `2px solid ${SAGE[300]}`, overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
        <Typography sx={{ fontFamily: F, fontSize: '0.62rem', fontWeight: 700, letterSpacing: 1.2,
          textTransform: 'uppercase', color: SAGE[500], mb: 0.75 }}>{kind}</Typography>
        <Typography sx={{ fontFamily: SERIF, fontSize: '1.05rem', fontWeight: 500, color: SAGE[800],
          lineHeight: 1.3, mb: 1 }}>{title}</Typography>
        <Typography sx={{ fontFamily: SERIF, fontSize: '0.78rem', fontStyle: 'italic', color: SAGE[500],
          lineHeight: 1.5 }}>{sub}</Typography>
        {started && openHooks.length > 0 && (
          <Typography sx={{ fontFamily: F, fontSize: '0.72rem', fontWeight: 600, color: SAGE[400], mt: 1 }}>
            {openHooks.length} {openHooks.length === 1 ? 'thread' : 'threads'} waiting
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 'auto', px: 2.5, py: 1.25, borderTop: `1px solid ${SAGE[100]}`,
        display: 'flex', alignItems: 'center', gap: 1 }}>
        {loading ? (
          <CircularProgress size={16} sx={{ color: SAGE[400] }} />
        ) : (
          <>
            <Button size="small" variant="contained" onClick={() => setModalOpen(true)}
              sx={{ fontFamily: F, textTransform: 'none', bgcolor: SAGE[500], '&:hover': { bgcolor: SAGE[600] } }}>
              {started ? 'Quick bite' : 'Start'}
            </Button>
            {started && (
              <Button size="small" onClick={() => navigate('/interview')}
                sx={{ fontFamily: F, textTransform: 'none', color: SAGE[600] }}>
                Continue at length →
              </Button>
            )}
          </>
        )}
      </Box>

      <InterviewModal open={modalOpen} entryMode="backfill"
        onClose={() => { setModalOpen(false); refresh(); }} />
    </Box>
  );
};

export default InterviewContinueCard;
